import React from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Avatar,
  Chip,
  useTheme,
} from "@mui/material";
import {
  CheckCircleOutline,
  Email,
  Phone,
  Business,
  AssignmentInd,
  Tune,
} from "@mui/icons-material";

const ProfileTab = ({ user, tradePreference }) => {
  const theme = useTheme();
  // console.log("User:", user);
  // console.log("Trade Preference:", tradePreference);
  if (!user) {
    return <Typography color="error">User data is not available.</Typography>;
  }

  // Add cache-busting to prevent browser caching of profile images
  const getProfileImageUrl = () => {
    if (!user.profile_image_url) return undefined;
    const timestamp = new Date().getTime();
    return user.profile_image_url.includes("?")
      ? `${user.profile_image_url}&t=${timestamp}`
      : `${user.profile_image_url}?t=${timestamp}`;
  };

  const getInitials = (name) => {
    if (!name) return "";
    return name
      .trim()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase())
      .join("");
  };
  // Map your fields to icons
  const infoFields = [
    {
      label: "Email",
      value: user.email,
      icon: <Email sx={{ color: theme.palette.scraptradeYellow.main }} />,
    },
    {
      label: "Contact",
      value: user.phone,
      icon: <Phone sx={{ color: theme.palette.scraptradeYellow.main }} />,
    },
    {
      label: "Company Name",
      value: user.company_name,
      icon: <Business sx={{ color: theme.palette.scraptradeYellow.main }} />,
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        bgcolor: theme.palette.background.default,
      }}
    >
      {/* Top Banner */}
      <Box
        sx={{
          position: "relative",
          background: `linear-gradient(135deg, ${theme.palette.scraptradeYellow.main}, ${theme.palette.scraptradeYellow.dark})`,
          height: { xs: 120, md: 160 },
        }}
      >
        <Avatar
          src={getProfileImageUrl()}
          sx={{
            width: { xs: 80, md: 120 },
            height: { xs: 80, md: 120 },
            border: `3px solid ${theme.palette.common.white}`,
            boxShadow: theme.shadows[4],
            position: "absolute",
            bottom: { xs: -40, md: -60 },
            left: "50%",
            transform: "translateX(-50%)",
            bgcolor: theme.palette.scraptradeYellow.main, // shows when no image
            fontSize: { xs: "1.5rem", md: "2.5rem" },
            fontWeight: "bold",
          }}
        >
          {getInitials(user?.name)}
        </Avatar>
      </Box>

      <Box sx={{ p: { xs: 2, md: 4 }, mt: { xs: 6, md: 8 } }}>
        {/* Name & Role */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {user.name}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            textAlign: "center",
            mb: 3,
          }}
        >
          {user.role}
        </Typography>

        {/* Info Section */}
        <Grid container direction="column" spacing={2}>
          {infoFields.map((item, i) => (
            <Grid item xs={12} key={i}>
              <Paper
                elevation={1}
                sx={{
                  p: { xs: 2, md: 4 },
                  borderRadius: 2,
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center" },
                    gap: 2,
                  }}
                >
                  {/* Icon + Label */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flex: { xs: "1 1 auto", sm: "0 0 150px" },
                    }}
                  >
                    {item.icon}
                    <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                      {item.label}
                    </Typography>
                  </Box>

                  {/* Value */}
                  <Typography
                    variant="body1"
                    sx={{
                      textAlign: { xs: "left", sm: "right" },
                      wordBreak: "break-word",
                    }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}

          {/* KYC Verification */}
          <Grid item xs={12}>
            <Paper
              elevation={1}
              sx={{
                p: { xs: 2, md: 4 },
                borderRadius: 2,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flex: { xs: "1 1 auto", sm: "0 0 150px" },
                  }}
                >
                  <AssignmentInd
                    sx={{ color: theme.palette.scraptradeYellow.main }}
                  />
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Verification Status
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography color="text.secondary">
                    {user.kycVerification}
                  </Typography>
                  <CheckCircleOutline
                    sx={{
                      color: theme.palette.success.main,
                      fontSize: "1.2rem",
                    }}
                  />
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Trade Preferences */}
          <Grid item xs={12}>
            <Paper
              elevation={1}
              sx={{
                p: { xs: 2, md: 4 },
                borderRadius: 2,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flex: { xs: "1 1 auto", sm: "0 0 170px" },
                  }}
                >
                  <Tune sx={{ color: theme.palette.scraptradeYellow.main }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Trade Preferences
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    justifyContent: { xs: "flex-start", sm: "flex-end" },
                  }}
                >
                  {(tradePreference || []).map((pref, index) => (
                    <Chip
                      key={index}
                      label={pref.name}
                      sx={{
                        background: `linear-gradient(45deg, ${theme.palette.scraptradeYellow.main}, ${theme.palette.scraptradeYellow.dark})`,
                        color: theme.palette.common.white,
                        fontWeight: "bold",
                        height: 28,
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default ProfileTab;
